import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createEmailTemplate,
  getAllEmailTemplate,
  getEmailTemplateByID,
  updateEmailTemplate,
} from "./services/emailTemplate.services";
import { clearEmailTemplateDetails } from "./emailTemplateSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";

const CreateEmailTemplate: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const EmailTemplateID = useAppSelector((state) => state.drawer.data);
  const EmailTemplateDetail = useAppSelector(
    (state) => state.emailTemplate.EmailTemplateDetail,
  );
  const loading = useAppSelector((state) => state.emailTemplate.formLoading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [EmailTemplateDetail]);

  useEffect(() => {
    if (EmailTemplateID) {
      dispatch(getEmailTemplateByID(EmailTemplateID));
    }
    return () => {
      dispatch(clearEmailTemplateDetails());
    };
  }, [EmailTemplateID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Subject: values.Subject ?? "",
      Content: values.Content ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (EmailTemplateID) {
        const editPayload = {
          ...formData,
          ID: EmailTemplateID,
        };

        const response = await dispatch(updateEmailTemplate(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllEmailTemplate());
          dispatch(clearEmailTemplateDetails());
        }
      } else {
        const response = await dispatch(createEmailTemplate(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllEmailTemplate());
          dispatch(clearEmailTemplateDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={EmailTemplateDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {EmailTemplateID ? "Update Email Template" : "Add Email Template"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Subject"
                    label="Subject"
                    placeholder="Enter email subject..."
                    component={FormTextField}
                    validator={min2CharValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Content"
                    label="Content"
                    component={FormRichTextField}
                    validator={requiredValidator}
                    astrike={true}
                    minHeight={500}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={EmailTemplateID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateEmailTemplate;
